import type { SignatureData } from "./types";
import { toJSON } from "./export";

/**
 * Sharing a signature straight into WhatsApp (or Mail, or AirDrop) via the native
 * share sheet. This is the whole point of the mobile flow: someone opens the link on
 * their phone, signs, taps Share, picks a contact. Nothing is uploaded — the file
 * goes only where they send it.
 *
 * navigator.share with files is iOS Safari 15+ and Android Chrome. Desktop browsers
 * largely lack it, hence the download fallback.
 */

export function signatureFile(data: SignatureData): File {
  return new File([toJSON(data)], "signature.json", { type: "application/json" });
}

/** Feature-detect with the real file: canShare() checks the payload, not just the API. */
export function canShare(file: File): boolean {
  return (
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  );
}

export type ShareOutcome = "shared" | "cancelled" | "unsupported";

/**
 * Must be called directly from a user gesture — iOS rejects a share that comes back
 * after an await. `signatureFile` is synchronous precisely so this stays in-gesture.
 */
export async function share(file: File): Promise<ShareOutcome> {
  if (!canShare(file)) return "unsupported";

  try {
    await navigator.share({
      files: [file],
      // Some targets show the title/text, some ignore it. WhatsApp attaches the file
      // and drops the rest, which is fine — the filename carries the meaning.
      title: "Signature",
      text: "My signature for mjkm.co.uk",
    });
    return "shared";
  } catch (error) {
    // Dismissing the sheet throws AbortError. That's a normal outcome, not a failure,
    // and must not be surfaced as an error.
    if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
    throw error;
  }
}
